<template>
  <AdminLayout page="Users" title="Users">
    <div v-if="isPropertyLoading"><Loading /></div>
    <div v-else-if="canViewUsers">
      <BackButton class="block" :to="`/${bldgStore.siteCode}/admin`"> Admin Home </BackButton>
      <div class="pb-2">
        <BaseCheckbox v-model="hideDisabledUsers" label="Hide Inactive Users" />
      </div>

      <input
        v-model.lazy="searchString"
        placeholder="Search"
        class="shadow appearance-none border rounded py-1 mt-16 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
      />
      <SearchButton :v-on="search" />
      <div v-if="!user.isTenantAdmin" class="inline-block">
        <BaseSelect
          defaultText="All Tenants"
          class="px-4 mb-1 text-sm bg-white"
          v-model="filteredTenant"
          :options="tenantNames"
        />
      </div>

      <table v-if="people" class="table-auto w-full mt-4 border-2 shadow text-sm">
        <thead class="font-sm font-bold">
          <tr>
            <td class="cursor-pointer" @click="ChangeSort(ColumnsToSort.FirstName)">
              <span class="xl:inline hidden">First </span>Name
              <UpDownArrow
                class="float-right"
                v-if="columnToSort == ColumnsToSort.FirstName"
                :isUp="!isSortingDown"
              />
            </td>
            <td class="cursor-pointer" @click="ChangeSort(ColumnsToSort.LastName)">
              Last Name
              <UpDownArrow
                class="float-right"
                v-if="columnToSort == ColumnsToSort.LastName"
                :isUp="!isSortingDown"
              />
            </td>
            <td class="cursor-pointer" @click="ChangeSort(ColumnsToSort.Role)">
              Role
              <UpDownArrow
                class="float-right"
                v-if="columnToSort == ColumnsToSort.Role"
                :isUp="!isSortingDown"
              />
            </td>
            <td class="cursor-pointer" @click="ChangeSort(ColumnsToSort.Email)">
              Email
              <UpDownArrow
                class="float-right"
                v-if="columnToSort == ColumnsToSort.Email"
                :isUp="!isSortingDown"
              />
            </td>
            <td class="cursor-pointer" @click="ChangeSort(ColumnsToSort.PrimaryPhone)">
              Primary Phone
              <UpDownArrow
                class="float-right"
                v-if="columnToSort == ColumnsToSort.PrimaryPhone"
                :isUp="!isSortingDown"
              />
            </td>
            <td class="cursor-pointer" @click="ChangeSort(ColumnsToSort.MobilePhone)">
              Mobile Phone
              <UpDownArrow
                class="float-right"
                v-if="columnToSort == ColumnsToSort.MobilePhone"
                :isUp="!isSortingDown"
              />
            </td>
            <td class="cursor-pointer" @click="ChangeSort(ColumnsToSort.Floor)">
              Floor
              <UpDownArrow
                class="float-right"
                v-if="columnToSort == ColumnsToSort.Floor"
                :isUp="!isSortingDown"
              />
            </td>
            <td
              v-if="filteredTenant == null || filteredTenant.length == 0"
              class="cursor-pointer"
              @click="ChangeSort(ColumnsToSort.Tenant)"
            >
              Tenant
              <UpDownArrow
                class="float-right"
                v-if="columnToSort == ColumnsToSort.Tenant"
                :isUp="!isSortingDown"
              />
            </td>
            <th v-if="canEditUsers"></th>
            <!-- edit button -->
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(person, i) in sortedPeople"
            :key="person.loginEmail"
            class="font-xs font-mono hover:border-sky-800 border-bottom-2 hover:bg-sky-300"
            :class="i % 2 ? 'bg-stone-200' : ''"
          >
            <td>{{ person.firstName }}</td>
            <td>
              {{ person.lastName
              }}<span v-if="person.dateDisabled" class="text-red-500"> (Inactive)</span>
            </td>
            <td>{{ person.roleAlias }}</td>
            <td>
              <a v-bind:href="'mailto:' + person.loginEmail">{{ person.loginEmail }}</a>
            </td>
            <td>
              <a v-bind:href="'tel:' + person.primaryPhone">{{
                formatPhoneNumber(person.primaryPhone)
              }}</a>
            </td>
            <td>
              <a v-bind:href="'tel:' + person.mobilePhone">{{
                formatPhoneNumber(person.mobilePhone)
              }}</a>
            </td>
            <td>{{ person.primaryFloor }}</td>
            <td v-if="filteredTenant == null || filteredTenant.length == 0">{{ person.tenant }}</td>
            <td v-if="canEditUsers">
              <button
                class="block ml-auto my-1 text-blue-500 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded"
                @click="editUser(person.personId)"
              >
                Edit
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <EditPopup
        entity-name="User"
        :is-visible="isPopupVisible"
        :is-edit-mode="isEditing"
        :save-error="saveError"
        :delete-enabled="true"
        :deleteButtonText="userIsInactive ? 'Reactivate User' : 'Deactivate User'"
        @close="
          isPopupVisible = false;
          search();
        "
        @save="saveClick"
        @delete="PersonActiveToggleClick"
      >
        <TabGroup>
          <TabList>
            <Tab v-slot="{ selected }">
              <button
                :class="[
                  'w-full py-2 px-4 font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-blue-300 text-black shadow py-3'
                    : 'bg-gray-100 text-gray-800 hover:bg-blue-100  mb-1',
                ]"
              >
                General
              </button>
            </Tab>
            <Tab v-slot="{ selected }">
              <button
                :class="[
                  'w-full py-2 px-4 font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-blue-300 text-black shadow py-3'
                    : 'bg-gray-100 text-gray-800 hover:bg-blue-100  mb-1',
                ]"
              >
                Alert
              </button>
            </Tab>
            <Tab
              v-if="
                isOnQAOrLocal() &&
                (selectedPersonIsTenantAdmin || selectedPermissionOptions.length == 0) &&
                !user.isTenantAdmin
              "
              v-slot="{ selected }"
            >
              <button
                :class="[
                  'w-full py-2 px-4 font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-blue-300 text-black shadow py-3'
                    : 'bg-gray-100 text-gray-800 hover:bg-blue-100  mb-1',
                ]"
              >
                Permissions
              </button>
            </Tab>
          </TabList>
          <TabPanels class="border border-blue-300 p-8">
            <TabPanel>
              <div class="sm:flex">
                <div class="basis-1/2 sm:pr-2">
                  <BaseInput
                    v-model="personFirstName"
                    :error="errors.personFirstName"
                    label="First Name"
                    type="text"
                  />
                </div>
                <div class="basis-1/2 sm:pl-2">
                  <BaseInput
                    v-model="personLastName"
                    :error="errors.personLastName"
                    label="Last Name"
                    type="text"
                  />
                </div>
              </div>
              <div class="sm:flex">
                <div class="basis-1/2 sm:pr-2">
                  <BaseInput
                    v-model="personPrimaryPhone"
                    :error="errors.personPrimaryPhone"
                    label="Primary Phone"
                    type="text"
                  />
                </div>
                <div class="basis-1/2 sm:pl-2">
                  <BaseInput
                    v-model="personMobilePhone"
                    :error="errors.personMobilePhone"
                    label="Mobile Phone"
                    type="text"
                  />
                </div>
              </div>
              <div class="mt-4" />

              <BaseCheckboxGroup
                :model-value="selectedTrainingProgramOptions"
                :options="trainingProgramOptions"
                label="Training Programs"
                class="md:flex flex-wrap"
                checkbox-class="basis-1/2 xl:basis-1/3"
              />

              <div class="mt-4" />
              <BaseSelect
                v-if="!user.isTenantAdmin"
                label="Tenant"
                v-model="personTenantId"
                :error="errors.personTenantId"
                :options="tenants"
                :default-option="({ name: '--Please Select--', value: 0 } as NameValue)"
              />
              <div class="xl:flex">
                <div class="basis-1/2 xl:pr-2">
                  <BaseSelect
                    label="Primary Floor"
                    v-model="personPrimaryFloor"
                    :error="errors.personPrimaryFloor"
                    :options="property?.floorList"
                  />
                </div>
                <div class="basis-1/2 xl:pl-2">
                  <BaseSelect
                    label="Building Role"
                    v-model="personSiteRoleId"
                    :error="errors.personSiteRoleId"
                    :options="siteRoles"
                    :default-option="({ name: '--Please Select--', value: 0 } as NameValue)"
                  />
                  <div class="mt-2" />
                </div>
              </div>

              <div class="mt-4" />

              <div class="mt-2 md:flex flex-wrap">
                <div class="md:basis-1/2 xl:basis-1/3">
                  <BaseCheckbox
                    v-model="personIsMobilityImpaired"
                    label="Has a Mobility Impairment"
                  />
                </div>
                <div class="md:basis-1/2 xl:basis-1/3">
                  <BaseCheckbox v-model="personIsTenantContact" label="Is Tenant Contact" />
                </div>
              </div>

              <div class="mt-4" />

              <div class="mt-2 md:flex flex-wrap">
                <BaseCheckbox
                  class="md:basis-1/2 xl:basis-1/3"
                  v-if="property?.isStaffDirEnabled && !user.isTenantAdmin"
                  v-model="personIsVisibleStaffDirectory"
                  label="Is Visible in Staff Directory"
                  :disabled="!getSiteRoleById(personSiteRoleId)?.isStaffRole"
                />
              </div>
            </TabPanel>
            <TabPanel>
              <div class="lg:flex">
                <div class="basis-1/2 lg:pr-2">
                  <BaseInput
                    v-model="personAlertSMS"
                    :error="errors.personAlertSMS"
                    label="Alert SMS"
                    type="text"
                  />
                </div>
                <div class="basis-1/2 lg:pl-2">
                  <BaseInput
                    v-model="personAlertEmail"
                    :error="errors.personAlertEmail"
                    label="Alert Email"
                    type="text"
                  />
                </div>
              </div>
              <div>
                <p class="mt-6 mb-3 text-sm">Select floors that you want notifications for:</p>
                <div class="sm:flex mt-1">
                  <div class="lg:grow-0 my-auto pr-4">
                    <BaseCheckbox v-model="selectAllAlertFloors" label="All Floors" />
                  </div>

                  <div class="sm:flex grow my-auto mt-2 sm:mt-0">
                    <Listbox
                      class="grow h-max"
                      v-model="selectedAlertFloors"
                      multiple
                      :disabled="selectAllAlertFloors"
                    >
                      <div class="relative">
                        <ListboxButton
                          class="relative w-full py-2 pl-3 pr-10 text-left bg-white border cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                        >
                          <span
                            :class="[
                              selectAllAlertFloors ? 'text-gray-500' : 'text-gray-800',
                              'text-md',
                            ]"
                          >
                            {{
                              selectAllAlertFloors
                                ? "All floors selected"
                                : selectedAlertFloors.length > 0
                                ? selectedAlertFloors.join(", ")
                                : "-- select floors --"
                            }}
                          </span>
                          <span
                            class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                          >
                            <i-heroicons-solid-selector
                              :class="[
                                selectAllAlertFloors ? 'text-gray-300' : 'text-gray-600',
                                'w-5 h-5 text-2xl',
                              ]"
                              aria-hidden="true"
                            />
                          </span>
                        </ListboxButton>
                        <transition
                          leave-active-class="transition duration-100 ease-in"
                          leave-from-class="opacity-100"
                          leave-to-class="opacity-0"
                        >
                          <ListboxOptions
                            class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                          >
                            <ListboxOption
                              v-for="floor in property?.floorList"
                              :key="floor"
                              :value="floor"
                              as="template"
                            >
                              <li
                                :class="[
                                  selectedAlertFloors.includes(floor)
                                    ? 'border-1 bg-sky-300 border-blue-500'
                                    : 'hover:bg-blue-200',
                                  'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                                ]"
                              >
                                {{ floor }}
                              </li>
                            </ListboxOption>
                          </ListboxOptions>
                        </transition>
                      </div>
                    </Listbox>
                    <BaseButton
                      class="rounded-none text-sm"
                      @click="if (!selectAllAlertFloors) selectedAlertFloors = [];"
                    >
                      Clear Selection
                    </BaseButton>
                  </div>
                </div>
              </div>

              <div class="mt-4" />
            </TabPanel>
            <TabPanel>
              <BaseCheckboxGroup
                :model-value="selectedPermissionOptions"
                :options="tenantPermissionOptions"
                :label="
                  'Permissions for ' + tenants.filter((t) => t.value == personTenantId)[0].name
                "
                class="md:flex flex-wrap"
                checkbox-class="basis-1/2 xl:basis-1/3"
              />
            </TabPanel>
          </TabPanels>
        </TabGroup>
        <Loading v-if="isPersonLoading && false" class="bg-red-400" />
      </EditPopup>
    </div>
    <div v-else>You need permission to view this page.</div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/vue";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue";
import {
  ClientAdminSavePersonCommand,
  PeopleSearchDTO,
  PermissionType,
  PersonDTO,
  RegistrationDTO,
  TenantAdminSavePersonCommand,
} from "../../store/generatedFetchClient";
import { usePersonStore } from "../../store/personStore";
import { useBldgStore } from "../../store/bldgStore";
import { useUserStore } from "../../store/userStore";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useTrainingStore } from "../../store/trainingStore";
import { compareFloors, formatPhoneNumber, isOnQAOrLocal } from "../../helpers/index";
import { NameValue } from "../../sharedTypes";

const personStore = usePersonStore();
const bldgStore = useBldgStore();
const trainingStore = useTrainingStore();

const userStore = useUserStore();
var canViewUsers = computed(() => userStore.hasPermission(PermissionType.ViewUsers));
var canEditUsers = computed(() => userStore.hasPermission(PermissionType.EditUsers));

var isPropertyLoading = computed(() => bldgStore.isLoading);
let isPersonLoading = computed(() => {
  //console.log(" > >isPersonLoading==" + personStore.isLoading);
  return personStore.isLoading;
});
let searchString = ref("");
let property = ref(bldgStore.building);
let user = computed(() => userStore.user);
let people = ref(null as PeopleSearchDTO[]);
var trainingPrograms = computed(() => trainingStore.portalTrainingPrograms);

let selectedPerson = ref(null as PersonDTO);

var sortedPeople = computed<PeopleSearchDTO[]>(() => {
  let results = people.value?.slice();

  console.log("filteredTenant", filteredTenant.value);
  if (filteredTenant.value != null && filteredTenant.value.length > 0)
    results = results.filter((result) => result.tenant == filteredTenant.value);

  switch (columnToSort.value) {
    case ColumnsToSort.FirstName:
      results = results.sort((a, b) => {
        if (a.firstName == b.firstName) {
          return (b.lastName ?? "").localeCompare(a.lastName ?? "");
        }
        return (b.firstName ?? "").localeCompare(a.firstName ?? "");
      });
      break;
    case ColumnsToSort.LastName:
      results = results.sort((a, b) => {
        if (a.lastName == b.lastName) {
          return (b.firstName ?? "").localeCompare(a.firstName ?? "");
        }
        return (b.lastName ?? "").localeCompare(a.lastName ?? "");
      });
      break;
    case ColumnsToSort.Email:
      results = results.sort((a, b) => (b.loginEmail ?? "").localeCompare(a.loginEmail ?? ""));
      break;
    case ColumnsToSort.Role:
      results = results.sort((a, b) => (b.roleAlias ?? "").localeCompare(a.roleAlias ?? ""));
      break;
    case ColumnsToSort.Floor:
      // Args swapped (b, a) to match this page's other columns, which sort Z→A before reverse
      results = results.sort(
        (a, b) =>
          compareFloors(b.primaryFloor, a.primaryFloor) ||
          (b.lastName ?? "").localeCompare(a.lastName ?? "") ||
          (b.firstName ?? "").localeCompare(a.firstName ?? "")
      );
      break;
    case ColumnsToSort.PrimaryPhone:
      results = results.sort((a, b) => {
        if (!b.primaryPhone) {
          return 1;
        } else if (!a.primaryPhone) {
          return -1;
        } else {
          return formatPhoneNumber(b.primaryPhone ?? "").localeCompare(
            formatPhoneNumber(a.primaryPhone ?? "")
          );
        }
      });
      break;
    case ColumnsToSort.MobilePhone:
      results = results.sort((a, b) => {
        if (!b.mobilePhone) {
          return 1;
        } else if (!a.mobilePhone) {
          return -1;
        } else {
          return formatPhoneNumber(b.mobilePhone ?? "").localeCompare(
            formatPhoneNumber(a.mobilePhone ?? "")
          );
        }
      });
      break;
    case ColumnsToSort.Tenant:
      results = results.sort((a, b) => (b.tenant ?? "").localeCompare(a.tenant ?? ""));
      break;
  }

  if (isSortingDown.value) {
    results = results.reverse();
  }

  return results;
});

var filteredTenant = ref<string>("");

enum ColumnsToSort {
  FirstName,
  LastName,
  Email,
  Role,
  Floor,
  PrimaryPhone,
  MobilePhone,
  Tenant,
}

var columnToSort = ref<ColumnsToSort>(ColumnsToSort.FirstName);
var isSortingDown = ref<boolean>(true);

function ChangeSort(column: ColumnsToSort) {
  if (column == columnToSort.value) {
    isSortingDown.value = !isSortingDown.value;
  } else {
    columnToSort.value = column;
    isSortingDown.value = true;
  }
  //console.log(`Sort changed. Now sorting by col #${columnToSort.value} ${isSortingDown.value ? "down" : "up"}`);
}
let hideDisabledUsers = ref(true);

onMounted(search);
watch(searchString, search);
watch(property, search);
watch(hideDisabledUsers, search);
async function search() {
  if (property.value != null) {
    var peopleList = await personStore.searchPeople(
      property.value.portalId,
      searchString.value,
      true
    );
    people.value = hideDisabledUsers.value
      ? peopleList?.filter((p) => !p.dateDisabled)
      : peopleList;
  }
}

onMounted(loadPortalData);
async function loadPortalData() {
  await trainingStore.getTrainingProgramsByPortal(property.value?.portalId);

  if (user.value.isTenantAdmin) {
    filteredTenant.value = user.value.tenantName;
  }
}

let isPopupVisible = ref(false);
let isEditing = ref(false);
let saveError = ref("");

async function selectPerson(personId: number) {
  console.log(`Trying to load personId`, personId);
  selectedPerson.value = await personStore.getPerson(personId);

  if (selectedPerson.value) {
    let permissions = selectedPerson.value.registrations?.find(
      (r) => r.portalId === property.value?.portalId
    ).permissions;
    selectedTrainingProgramOptions.value = permissions
      .filter((p) => p.trainingProgramId != null)
      .map((p) => p.trainingProgramId);
    selectedPermissionOptions.value = permissions
      .filter((p) => p.permissionType != null)
      .map((p) => p.permissionType);

    userIsInactive.value =
      (selectedPerson.value.dateDisabled != null &&
        selectedPerson.value.dateDisabled != undefined) ??
      false;
    isHomePortal.value = portalRegistration.value?.isHomePortal;

    selectedPersonIsTenantAdmin.value = selectedPerson.value.registrations?.find(
      (r) => r.portalId === property.value?.portalId
    ).isTenantAdmin;

    console.log(`Loaded person`, selectedPerson.value);
  }
}

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    personFirstName: yup.string().required().max(50).label("First Name"),
    personLastName: yup.string().notRequired().max(50).label("Last Name"),
    personLoginEmail: yup.string().required().email().max(255).label("Login Email"),
    personPrimaryFloor: yup.string().nullable().notRequired().max(50).label("Primary Floor"),
    personSiteRoleId: yup
      .number()
      .min(1, "Building Role is a required field")
      .required()
      .label("Building Role"),
    personTenantId: yup
      .number()
      .required()
      .test(
        "valid-tenant-vr",
        (d) => `Tenant is required for VR-enabled portals`,
        (value) => (property.value?.isVrtourEnabled ? value > 0 : true)
      )
      .label("Tenant"),
    personPrimaryPhone: yup.string().nullable().notRequired().max(50).label("Primary Phone"),
    personMobilePhone: yup.string().nullable().notRequired().max(50).label("Mobile Phone"),
    personAlertSMS: yup.string().nullable().notRequired(),
    personAlertEmail: yup.string().nullable().notRequired(),
  }),
});

const { value: personFirstName } = useField<string>("personFirstName");
const { value: personLastName } = useField<string>("personLastName");
const { value: personLoginEmail } = useField<string>("personLoginEmail");
const { value: personPrimaryFloor } = useField<string | null>("personPrimaryFloor");
const { value: personTenantId } = useField<number>("personTenantId");
const { value: personSiteRoleId } = useField<number>("personSiteRoleId");
const { value: personPrimaryPhone } = useField<string | null>("personPrimaryPhone");
const { value: personMobilePhone } = useField<string | null>("personMobilePhone");
const personIsMobilityImpaired = ref(false);
const personIsVrAdmin = ref(false);

const personIsTenantContact = ref(false);
const personIsVisibleStaffDirectory = ref(false);
const { value: personAlertSMS } = useField<string | null>("personAlertSMS");
const { value: personAlertEmail } = useField<string | null>("personAlertEmail");
const selectAllAlertFloors = ref(false);
const selectedAlertFloors = ref([] as string[]);
const userIsInactive = ref(false);
const isHomePortal = ref(false);

let portalRegistration = computed(() =>
  isPersonLoading.value
    ? ({} as RegistrationDTO)
    : selectedPerson.value?.registrations?.find((r) => r.portalId === property.value?.portalId)
);

let siteRoles = computed(() =>
  isPersonLoading.value
    ? ([] as NameValue[])
    : property.value?.siteRoles
        ?.filter(
          (r) =>
            !user.value.isTenantAdmin || !r.isStaffRole || personSiteRoleId.value == r.siteRoleId
        )
        .map(
          (r) =>
            ({
              name: r.roleAlias + (r.isStaffRole ? " (Staff)" : ""),
              value: r.siteRoleId,
            } as NameValue)
        )
);

let tenants = computed(() =>
  isPersonLoading.value
    ? ([] as NameValue[])
    : property.value?.tenants?.map((t) => ({ name: t.name, value: t.tenantId } as NameValue))
);

let tenantNames = computed<string[]>(() =>
  isPersonLoading.value ? ([] as string[]) : property.value.tenants.map((t) => t.name)
);

let trainingProgramOptions = computed(() =>
  isPersonLoading.value
    ? ([] as NameValue[])
    : trainingPrograms.value?.map(
        (p) =>
          ({
            name: p?.name,
            value: p?.trainingProgramId,
          } as NameValue)
      )
);
let selectedPersonIsTenantAdmin = ref(false);
let selectedTrainingProgramOptions = ref<number[]>();
let selectedPermissionOptions = ref<number[]>();

/// Edit User ************************************************************************************************
async function editUser(personId: number) {
  console.log("Editing user", personId);
  await selectPerson(personId);

  if (selectedPerson.value?.personId > 0) {
    personFirstName.value = selectedPerson.value?.firstName;
    personLastName.value = selectedPerson.value?.lastName;
    personLoginEmail.value = selectedPerson.value?.loginEmail;
    personPrimaryFloor.value = selectedPerson.value?.primaryFloor;
    personPrimaryPhone.value = selectedPerson.value?.primaryPhone;
    personMobilePhone.value = selectedPerson.value?.mobilePhone;
    personIsMobilityImpaired.value = selectedPerson.value?.isMobilityImpaired;
    personIsVrAdmin.value = selectedPerson.value?.isVrAdmin ?? false;

    personIsTenantContact.value = selectedPerson.value?.isTenantContact ?? false;
    personAlertEmail.value = selectedPerson.value?.contactEmail;
    personAlertSMS.value = selectedPerson.value?.contactSms;
    selectAllAlertFloors.value =
      selectedPerson.value?.alertSubscribedFloors == property?.value?.floorList.toString() ||
      selectedPerson.value?.alertSubscribedFloors == null;
    selectedAlertFloors.value =
      selectedPerson.value?.alertSubscribedFloors != null &&
      selectedPerson.value?.alertSubscribedFloors.length > 0
        ? selectedPerson.value?.alertSubscribedFloors.split(",")
        : [];

    personSiteRoleId.value = portalRegistration.value?.siteRoleId;
    personTenantId.value = portalRegistration.value?.tenantId;
    personIsVisibleStaffDirectory.value =
      portalRegistration.value?.isVisibleStaffDirectory &&
      getSiteRoleById(personSiteRoleId.value)?.isStaffRole;

    selectedTrainingProgramOptions.value = trainingPrograms.value
      .filter((t) =>
        portalRegistration.value?.permissions.find(
          (p) => p.trainingProgramId === t.trainingProgramId
        )
      )
      .map((p) => p.trainingProgramId);

    isHomePortal.value = portalRegistration.value?.isHomePortal;
    userIsInactive.value =
      (selectedPerson.value?.dateDisabled !== null &&
        selectedPerson.value?.dateDisabled !== undefined) ??
      false;

    saveError.value = "";
    isEditing.value = true;
    isPopupVisible.value = true;
  }
}

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  if (user.value.isTenantAdmin) {
    let savePerson: TenantAdminSavePersonCommand = {
      personId: selectedPerson.value?.personId,
      portalId: property.value?.portalId,
      isHomePortal: isHomePortal.value,
      loginEmail: personLoginEmail.value,
      firstName: personFirstName.value,
      lastName: personLastName.value,
      primaryFloor: personPrimaryFloor.value,
      siteRoleId: personSiteRoleId.value,
      mobilePhone: personMobilePhone.value,
      primaryPhone: personPrimaryPhone.value,
      contactEmail: personAlertEmail.value,
      contactSms: personAlertSMS.value,
      alertSubscribedFloors: selectAllAlertFloors.value
        ? null
        : selectedAlertFloors.value?.toString(),
      isTenantContact: personIsTenantContact.value,

      trainingProgramIDs: selectedTrainingProgramOptions.value,

      isMobilityImpaired: personIsMobilityImpaired.value,
      dateDisabled: userIsInactive.value ? new Date() : undefined,
    };

    console.log("Saving person (tenant admin)", savePerson);
    saveError.value = "";

    if (property.value && property.value?.portalId) {
      let possibleError = await personStore.tenantAdminSavePerson(savePerson);

      if (!possibleError) {
        search();

        isPopupVisible.value = false;
      } else {
        //handle error
        saveError.value = possibleError;
      }
    }
  } else {
    let savePerson: ClientAdminSavePersonCommand = {
      personId: selectedPerson.value?.personId,
      portalId: property.value?.portalId,
      isHomePortal: isHomePortal.value,
      loginEmail: personLoginEmail.value,
      firstName: personFirstName.value,
      lastName: personLastName.value,
      primaryFloor: personPrimaryFloor.value,
      siteRoleId: personSiteRoleId.value,
      mobilePhone: personMobilePhone.value,
      tenantId: personTenantId.value > 0 ? personTenantId.value : null,
      primaryPhone: personPrimaryPhone.value,
      contactEmail: personAlertEmail.value,
      contactSms: personAlertSMS.value,
      alertSubscribedFloors: selectAllAlertFloors.value
        ? null
        : selectedAlertFloors.value?.toString(),
      isTenantContact: personIsTenantContact.value,
      isVrAdmin: personIsVrAdmin.value,

      isVisibleStaffDirectory: personIsVisibleStaffDirectory.value,
      trainingProgramIDs: selectedTrainingProgramOptions.value,
      permissionTypeIDs: selectedPermissionOptions.value,

      isMobilityImpaired: personIsMobilityImpaired.value,
      dateDisabled: userIsInactive.value ? new Date() : undefined,
    };

    console.log("Saving person (client admin)", savePerson);
    saveError.value = "";

    if (property.value && property.value?.portalId) {
      let possibleError = await personStore.clientAdminSavePerson(savePerson);

      if (!possibleError) {
        search();

        isPopupVisible.value = false;
      } else {
        //handle error
        saveError.value = possibleError;
      }
    }
  }
}

async function PersonActiveToggleClick() {
  let result;

  if (userIsInactive.value) {
    result = user.value.isTenantAdmin
      ? await personStore.tenantReactivatePerson(
          selectedPerson.value.personId,
          property.value.propertyId
        )
      : await personStore.reactivatePerson(
          selectedPerson.value.personId,
          property.value.propertyId
        );
  } else {
    result = user.value.isTenantAdmin
      ? await personStore.tenantDeactivatePerson(
          selectedPerson.value.personId,
          property.value.propertyId
        )
      : await personStore.deactivatePerson(
          selectedPerson.value.personId,
          property.value.propertyId
        );
  }

  if (result != null) {
    saveError.value = result;
  } else {
    await search();
    isPopupVisible.value = false;
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

var getSiteRoleById = (siteRoleId: number) =>
  property.value?.siteRoles?.find((r) => r.siteRoleId == siteRoleId);

let tenantPermissionOptions = [
  { name: "Can View Training Report", value: PermissionType.ViewTrainingReport },
  { name: "Can View Tenant Roster", value: PermissionType.ViewPortalRoster },
  { name: "Can View Mobility Report", value: PermissionType.ViewMobilityReport },
  { name: "Can View VR Report", value: PermissionType.ViewVRReport },
  { name: "Can View VR Filter Report", value: PermissionType.ViewVRFilterReport },
  { name: "Can View Users", value: PermissionType.ViewUsers },
  { name: "Can Edit Users", value: PermissionType.EditUsers },
];
</script>
